// Pure helpers for the node `builder` metadata (capabilities, option sources, key forms).

const TEMPLATE = /\{(\w+)\}/g;

/** Replace `{field}` placeholders in a request template with the current values. */
export const fillTemplate = (template, context = {}) => {
  if (typeof template !== "string") return template;

  return template.replace(TEMPLATE, (match, key) => {
    const value = context?.[key];
    return value === undefined || value === null ? "" : String(value);
  });
};

/**
 * `remote-select` sources.
 *
 * Newer catalogs declare `sources: [{source, label, value_field, label_field, …}]` so one control
 * can offer e.g. Slack Channels and People. Fall back to a single `source`.
 */
export const optionSources = (controlOptions = {}) => {
  const list = Array.isArray(controlOptions?.sources) ? controlOptions.sources : [];

  if (list.length > 0) {
    return list
      .filter((entry) => entry && (entry.source ?? entry.value))
      .map((entry, index) => ({
        source: entry.source ?? entry.value,
        label: entry.label ?? entry.source ?? `Source ${index + 1}`,
        labelPrefix: entry.label_prefix ?? entry.labelPrefix ?? "",
        labelField: entry.label_field ?? entry.labelField ?? "name",
        valueField: entry.value_field ?? entry.valueField ?? "id",
        descriptionField: entry.description_field ?? entry.descriptionField ?? null,
      }));
  }

  if (controlOptions?.source) {
    return [
      {
        source: controlOptions.source,
        label: controlOptions.label ?? controlOptions.source,
        labelPrefix: controlOptions.label_prefix ?? "",
        labelField: controlOptions.label_field ?? "name",
        valueField: controlOptions.value_field ?? "id",
        descriptionField: controlOptions.description_field ?? null,
      },
    ];
  }

  return [];
};

/**
 * Build the options request from `control_options.request`.
 *
 * The catalog owns the URL shape (`endpoint`, `query` templates, pagination parameter names);
 * the frontend only substitutes `{source}` and `{field}` placeholders and appends the client id
 * and pagination values.
 *
 * @param {object} params
 * @param {object} [params.controlOptions]
 * @param {string|null} [params.source]
 * @param {object} [params.data] The node's resolved data (for `{field}` templates).
 * @param {string|null} [params.clientId]
 * @param {string|number|null} [params.cursor]
 * @param {number|null} [params.limit]
 * @returns {{endpoint: string, method: string, query: Record<string,string>, optionsField: string, cursorField: string}}
 */
export const buildOptionsRequest = ({
  controlOptions = {},
  source = null,
  data = {},
  clientId = null,
  cursor = null,
  limit = null,
} = {}) => {
  const request = controlOptions?.request ?? {};
  const endpoint = request.endpoint ?? controlOptions?.endpoint ?? "/admin/node-options";
  const method = request.method ?? "GET";
  const query = {};

  const templates = request.query ?? null;

  if (templates && Object.keys(templates).length > 0) {
    Object.entries(templates).forEach(([param, template]) => {
      const value = fillTemplate(template, { source, ...data });
      if (value !== "" && value !== undefined && value !== null) query[param] = value;
    });
  } else {
    // Legacy metadata without a request block.
    if (source) query.source = source;

    const service = controlOptions?.filter?.service ?? controlOptions?.service ?? null;
    if (service) query.service = service;

    const dependsOn = Array.isArray(controlOptions?.depends_on) ? controlOptions.depends_on : [];
    const keyField = dependsOn[0];
    if (keyField && data?.[keyField] !== undefined && data[keyField] !== null && data[keyField] !== "") {
      query.key = data[keyField];
    }
  }

  if (clientId) query[request.client_id_param ?? "client_id"] = clientId;
  if (cursor) query[request.cursor_param ?? "cursor"] = String(cursor);
  if (limit) query[request.limit_param ?? "limit"] = String(limit);

  return {
    endpoint,
    method,
    query,
    optionsField: request.options_field ?? "options",
    cursorField: request.cursor_field ?? "next_cursor",
  };
};

/** The display label for an option: prefix + label, with the description appended when present. */
export const optionLabel = (option, source = {}) => {
  const rawLabel = option?.label ?? option?.value ?? "";
  const label = `${source.labelPrefix ?? ""}${rawLabel}`;
  const description = option?.meta?.[source.descriptionField] ?? option?.[source.descriptionField];

  if (source.descriptionField && description) return `${label} — ${description}`;
  return label;
};

/**
 * The node data field that holds the key used for a capabilities lookup.
 *
 * Follows `builder.capabilities_key_param` (older: `capabilities_key_field` / `capabilities_key`).
 * When the catalog omits it, infer it from the parameters: the client-key picker's field. As a
 * last resort, use a data key that looks like a client key.
 */
export const capabilitiesKeyField = (builder = {}, definition = null, data = {}) => {
  // `capabilities_key_field` names the node field; `capabilities_key_param` is the query parameter
  // it is sent as (see `capabilitiesKeyParam`).
  const declared = builder?.capabilities_key_field ?? builder?.capabilities_key ?? null;
  if (declared) return declared;

  const picker = (definition?.parameters ?? []).find((parameter) => {
    const control = String(parameter?.control ?? "").toLowerCase();
    return ["client-key-picker", "client-key", "clientkey", "client-api-key"].includes(control);
  });

  if (picker?.name) return picker.name;

  return Object.keys(data ?? {}).find((key) => /client.?key/i.test(key)) ?? null;
};

/** The query parameter the capabilities key is sent as (`builder.capabilities_key_param`). */
export const capabilitiesKeyParam = (builder = {}) => builder?.capabilities_key_param ?? "key";

/**
 * The key value used for a capabilities lookup.
 *
 * Falls back to `default_data` exactly like the parameter renderer does, so a key that was never
 * explicitly written onto the node (it only has a catalog default) still counts as selected.
 */
export const capabilitiesKeyValue = (builder = {}, definition = null, data = {}) => {
  const field = capabilitiesKeyField(builder, definition, data);
  if (!field) return null;

  const parameter = (definition?.parameters ?? []).find((entry) => entry?.name === field);
  const value = data?.[field] ?? definition?.defaultData?.[field] ?? parameter?.default;
  return value === undefined || value === null || value === "" ? null : value;
};

/**
 * Per-operation capability flags.
 *
 * Accepts `{capabilities: {send_message: {ok}}}` or `{send_message: {ok}}` shapes and returns
 * `{send_message: true|false}`.
 */
export const capabilityMap = (capabilities) => {
  if (!capabilities || typeof capabilities !== "object") return {};

  const containers = [capabilities.capabilities, capabilities.operations, capabilities];

  const map = {};

  containers.forEach((container) => {
    if (!container || typeof container !== "object") return;

    Object.entries(container).forEach(([name, value]) => {
      if (value && typeof value === "object" && !Array.isArray(value) && "ok" in value) {
        map[name] = Boolean(value.ok);
      }
    });
  });

  return map;
};

/** Whether any of the named capabilities is blocked (false). `null` when unknown. */
export const capabilityBlocked = (capabilities, names = []) => {
  const map = capabilityMap(capabilities);
  const relevant = names.filter((name) => name in map);

  if (relevant.length === 0) return null;
  return relevant.some((name) => map[name] === false);
};

/** The missing scopes reported by a capabilities response. */
export const missingScopes = (capabilities) => {
  if (!capabilities || typeof capabilities !== "object") return [];

  const list =
    capabilities.missing_scopes ??
    capabilities.missing ??
    capabilities.missingScopes ??
    capabilities.required_missing ??
    [];

  return Array.isArray(list) ? list.filter((scope) => typeof scope === "string") : [];
};

/** A reinstall URL, when the backend provides one. */
export const reinstallUrl = (capabilities) =>
  capabilities?.reinstall_url ??
  capabilities?.install_url ??
  capabilities?.app_install_url ??
  capabilities?.slack_install_url ??
  null;

/** A human summary of the connected Slack workspace/bot. */
export const describeConnection = (capabilities) => {
  if (!capabilities || typeof capabilities !== "object") return null;

  const team = capabilities.team?.name ?? capabilities.team_name ?? capabilities.workspace ?? null;
  const bot = capabilities.bot?.name ?? capabilities.bot_name ?? capabilities.user?.name ?? capabilities.user_name ?? null;

  const parts = [team, bot].filter(Boolean);
  return parts.length > 0 ? parts.join(" · ") : null;
};

/** Build the key payload from a `client_key_form.fields` list and the form values. */
export const buildKeyPayload = (fields = [], values = {}, { service = null, clientId = null } = {}) => {
  const payload = {};

  (Array.isArray(fields) ? fields : []).forEach((field) => {
    const name = field?.name;
    if (!name) return;

    const value = values[name];
    if (value === undefined || value === null || value === "") return;

    payload[name] = value;
  });

  if (service) payload.service = service;
  if (clientId) payload.client_id = clientId;

  return payload;
};

export default optionSources;
